import { useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { BackLink } from "../components/back-link";
import { Button } from "../components/button";
import { H1 } from "../components/h1";
import { Layout } from "../components/layout";
import { useOnlineStatus } from "../hooks/use-online-status";
import { useView } from "../queries/view";
import { useLocale } from "../stores/locale";
import { Image as ImageType } from "../types/image";

export function View() {
  const { code } = useParams();
  const { locale: t } = useLocale();
  const navigate = useNavigate();
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOnline = useOnlineStatus();

  if (!code) {
    throw new Error("Code is required");
  }

  const startHold = () => {
    holdTimer.current = setTimeout(() => navigate(`/${code}/edit`), 4000);
  };

  const cancelHold = () => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  const { data, isLoading: isViewLoading } = useView(code, "user");
  const view = data?.view;
  const isFromCache = data?.isFromCache ?? false;

  return (
    <Layout>
      <BackLink />
      <div className="flex flex-col gap-5 flex-grow items-center mt-10">
        {view && (
          <>
            <H1
              className="select-none cursor-default"
              onMouseDown={startHold}
              onMouseLeave={cancelHold}
              onMouseUp={cancelHold}
              onTouchCancel={cancelHold}
              onTouchEnd={cancelHold}
              onTouchStart={startHold}
            >
              {view?.name ?? code}
            </H1>
            {isFromCache && (
              <p className="rounded bg-amber-500/20 px-3 py-1 text-center text-xs text-amber-300">
                {t.offline.cachedVersion}
              </p>
            )}
            <div className="flex flex-col gap-8">
              {view?.images.map((image, index) => (
                <Image image={image} key={index} />
              ))}
            </div>
          </>
        )}
        {!isViewLoading && !view && (
          <div className="flex flex-col gap-5 flex-grow items-center justify-center">
            {isOnline ? (
              <>
                <H1>{t.notFound.title}</H1>
                <p>
                  {t.notFound.description} <code>{code}</code>
                </p>
              </>
            ) : (
              <>
                <H1>{t.offline.notAvailable}</H1>
                <p className="text-center text-sm">{t.offline.notAvailableDescription}</p>
              </>
            )}
            <Button href="/" severity="secondary" size="sm" type="route">
              {t.back}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Image({ image }: { image: ImageType }) {
  const url = useMemo(() => {
    return URL.createObjectURL(
      new Blob([new Uint8Array(image.bytes as ArrayBuffer)], {
        type: "image/jpeg",
      }),
    );
  }, [image.bytes]);

  return (
    <div
      className="w-[200px] h-[200px] rounded-xl overflow-hidden bg-cover bg-center flex flex-col justify-end"
      style={{ backgroundImage: `url(${url})` }}
    >
      <p className="text-center text-black text-xl font-bold text-shadow-md text-shadow-amber-50">
        {image.title}
      </p>
    </div>
  );
}
